<template>
    <v-autocomplete
        v-model="selectedClient"
        :items="clients"
        :item-title="itemText || inputText"
        :item-value="itemValue || 'personId' || inputValue"
        :loading="isLoadingClients || loading"
        hide-no-data
        :label="label"
        :multiple="multiple"
        :chips="multiple"
        :closable-chips="closableChips"
        :return-object="!itemValue"
        variant="outlined"
        :density="dense ? 'compact' : 'comfortable'"
        hide-details
        :readonly="readonly"
        :persistent-placeholder="!disablePersistentPlaceholder"
        bg-color="white"
        :clearable="clearable"
        :disabled="disabled"
        :rules="rules || [requiredRule('select_user')]"
        :menu-icon="clients.length ? '$dropdown' : ''"
        @update:model-value="onModelValueChange"
        @update:search="onSearchInput"
        @blur="onBlur"
    >
        <template v-slot:item="{props, item}">
            <v-list-item
                v-bind="props"
                :title="itemText ? item.raw[itemText] : inputText(item.raw)"
                :subtitle="getSubtitle(item.raw)"
            >
                <template v-slot:title="{title}">
                    <div class="d-flex align-center">
                        <span v-html="getHighlightedTitle(title)" />
                    </div>
                </template>
            </v-list-item>
        </template>
        <template v-slot:prepend-item>
            <slot />
        </template>
    </v-autocomplete>
</template>

<script lang="ts">
    import {Component, Prop, toNative} from 'vue-facing-decorator'
    import {CompanyTypeToCommunicationInfoType} from '~/utils/api-data-mapings'
    import __ClientAutocomplete, {
        ClientAutocomplete as _ClientAutocomplete,
    } from '~/components/account/modals/newOrderRequest/ClientAutocomplete.vue'

    @Component({mixins: [__ClientAutocomplete]})
    export class PrivateClientAndCompaniesAutocomplete extends _ClientAutocomplete {
        @Prop({default: () => [], type: Array}) relationship
        @Prop({default: false, type: Boolean}) communicationsMode

        cancelTokenSources = []
        isSelecting = false

        async loadPrivateClients() {
            try {
                const cancelTokenSource = this.$axios.CancelToken.source()
                this.cancelTokenSources.push(cancelTokenSource)

                const rqPClients = {
                    pattern: this.searchInput,
                    limit: 10,
                    active: true,
                }

                return await this.$api.suggestPrivateClients.get(rqPClients, cancelTokenSource.token)
            } catch (e) {
                this.$toast.error(this.$t('error_message.load_error'))
            }
        }

        async loadCompanies(rq) {
            try {
                const cancelTokenSource = this.$axios.CancelToken.source()
                this.cancelTokenSources.push(cancelTokenSource)

                return await this.$api.companies.get(rq, cancelTokenSource.token)
            } catch (e) {
                this.$toast.error(this.$t('error_message.load_error'))
            }
        }

        async loadClients() {
            try {
                this.clients = []
                this.cancelTokenSources.forEach(cancelTokenSource => cancelTokenSource.cancel())
                this.cancelTokenSources = []
                this.isLoadingClients = true

                const companyRequests = this.relationship.map(relationship => ({
                    name: this.searchInput,
                    limit: 10,
                    order: 'ALPHABET',
                    ascending: true,
                    active: true,
                    ...(relationship && {relationship}),
                }))

                const [{persons}, ...rs] = await Promise.all([
                    this.loadPrivateClients(),
                    ...companyRequests.map(rq => this.loadCompanies(rq)),
                ])

                this.clients = [
                    ...persons.map(p => ({...p, id: p.personId})),
                    ...rs.map(({companies}) => companies).flat(),
                ]
            } catch (e) {
                /* empty */
            } finally {
                this.isLoadingClients = false
            }
        }

        // ZSH Override: Custom model value change handler
        onModelValueChange(val) {
            this.isSelecting = true
            this.change(val)
            // Reset the flag after a short delay to allow the autocomplete to settle
            setTimeout(() => {
                this.isSelecting = false
            }, 100)
        }

        // ZSH Override: Custom search input handler to prevent unwanted dropdown triggers
        async onSearchInput(val) {
            await this.$nextTick()

            // If we're in the middle of selecting, don't trigger new searches
            if (this.isSelecting) {
                return
            }

            // If we have a selected client, check if the search input matches the selected client
            if (this.selectedClient && !this.multiple) {
                const selectedDisplayText = this.inputText(this.selectedClient)
                // If the search value matches the selected client's display text, don't trigger new search
                if (val === selectedDisplayText) {
                    return
                }
                // If the search value is empty and we have a selected client, don't clear it
                if (val === '' || val === null || val === undefined) {
                    return
                }
            }

            // Only trigger search for actual user input
            this.searchInput = val
            this.clearResults()
            if (val?.length && !this.preloadAll) {
                await this.loadClients()
            }
        }

        // ZSH Override: Fix for value clearing issue
        async onInput(val) {
            // This method is kept for compatibility but delegates to onSearchInput
            return this.onSearchInput(val)
        }

        // ZSH Override: Fix for value clearing issue
        clearResults() {
            // Override to prevent clearing selected client when used as a filter
            // Only clear the clients array for new searches, but preserve the selected value
            if (!this.multiple) {
                // If we have a selected client, keep it in the clients array so it remains available
                if (this.selectedClient) {
                    this.clients = [this.selectedClient]
                } else {
                    this.clients = []
                }
            }
        }

        inputText(entry, selected = null, noPrefix = false) {
            const prefix = entry.prefix && !noPrefix ? entry.prefix : ''
            const firstName = entry.firstName ? entry.firstName : ''
            const lastName = entry.lastName ? entry.lastName : ''
            const companyName = entry.name

            let label = companyName
                ? companyName
                : (prefix ? `${prefix}. ` : '') + (firstName ? `${firstName} ` : '') + (lastName ? `${lastName}` : '')

            if (this.communicationsMode) {
                label += entry.type
                    ? ` (${this.$t(`accountType.${CompanyTypeToCommunicationInfoType(entry.type)}`)})`
                    : ` (${this.$t('private_clients')})`
            }

            return label
        }
    }

    export default {...toNative(PrivateClientAndCompaniesAutocomplete), ssrRender: __ClientAutocomplete.ssrRender}
</script>
